import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../contexts/i18n-context';

/**
 * Hook for translating text with automatic state management
 * Works like Google Translate - translates on-the-fly
 */
export function useTranslation() {
  const { t, tSync, language, setLanguage, isLoading, supportedLanguages, clearCache } = useI18n();
  const [translatedTexts, setTranslatedTexts] = useState<Map<string, string>>(new Map());

  /**
   * Translate text and automatically update when language changes
   */
  const translate = useCallback(async (text: string, fallback?: string): Promise<string> => {
    if (!text || !text.trim()) return text || fallback || '';
    if (language === 'en') return text;

    const cacheKey = `${text}:${language}`;
    
    // Check local cache first
    setTranslatedTexts(prev => {
      if (prev.has(cacheKey)) {
        return prev;
      }
      return prev;
    });
    
    const cached = translatedTexts.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Translate and cache
    const translated = await t(text, { fallback });
    setTranslatedTexts(prev => new Map(prev).set(cacheKey, translated));
    return translated;
  }, [language, t, translatedTexts]);

  /**
   * Translate text synchronously (uses cache only)
   */
  const translateSync = useCallback((text: string, fallback?: string): string => {
    return tSync(text, fallback);
  }, [tSync]);

  /**
   * Translate multiple texts at once
   */
  const translateBatch = useCallback(async (texts: string[]): Promise<string[]> => {
    if (language === 'en') return texts;
    
    return Promise.all(texts.map(text => translate(text)));
  }, [language, translate]);

  // Clear cache when language changes
  useEffect(() => {
    setTranslatedTexts(new Map());
  }, [language]);

  return {
    t: translate,
    tSync: translateSync,
    translateBatch,
    language,
    setLanguage,
    isLoading,
    supportedLanguages,
    clearCache,
  };
}

/**
 * Hook for translating text in components with automatic re-rendering
 */
export function useTranslatedText(text: string, fallback?: string): string {
  const { t, tSync, language } = useI18n();
  const [translated, setTranslated] = useState<string>(text || fallback || '');

  useEffect(() => {
    if (!text || !text.trim()) {
      setTranslated(fallback || '');
      return;
    }

    if (language === 'en') {
      setTranslated(text);
      return;
    }

    // Try to get from cache first
    const cached = tSync(text, fallback);
    if (cached && cached !== text && !cached.includes('MYMEMORY WARNING')) {
      setTranslated(cached);
      return;
    }

    // Translate asynchronously
    const translateAsync = async () => {
      try {
        const result = await t(text, { fallback });
        // Make sure result doesn't contain warnings
        if (result && !result.includes('MYMEMORY WARNING')) {
          setTranslated(result);
        } else {
          setTranslated(fallback || text);
        }
      } catch {
        setTranslated(fallback || text);
      }
    };

    translateAsync();
  }, [text, language, fallback, t, tSync]);

  return translated;
}

